/**
 * Social Media TanStack Query hooks.
 *
 * All queries use useApiQuery from dashboard-shared which:
 * - Auto-unwraps { success, data } API responses
 * - Returns just the typed data field
 * - Throws on success=false responses
 */

import { useApiQuery } from "@plerity/dashboard-shared/hooks";
import { keepPreviousData } from "@tanstack/react-query";
import { useClientBrandStore } from "../lib/client-brand";
import { isUuid } from "../lib/uuid";
import type {
  Account,
  AnalyticsOverview,
  AnalyticsSnapshot,
  CalendarPost,
  ChurnRiskData,
  CopywriterConfig,
  MediaItem,
  MonthlyReportData,
  OnboardingData,
  OrgSettings,
  PinterestBoard,
  Post,
  Template,
  UserSettings,
} from "../types/social";
import { socialKeys } from "./keys";
import { containerResponseGuard, objectResponseGuard } from "./schemas";

// ── Accounts ──────────────────────────────────────────────────────────────────

export function useAccounts(platform?: string) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params: Record<string, string> = {};
  if (platform) params.platform = platform;
  if (selectedBrandId) params.client_brand_id = selectedBrandId;
  const hasParams = Object.keys(params).length > 0;
  return useApiQuery<Account[]>(socialKeys.list("accounts", hasParams ? params : undefined), "/api/accounts", {
    // Connected accounts rarely change mid-session; a 30s staleTime keeps the
    // KPI card + Accounts panel from refetching on every tab switch.
    staleTime: 30_000,
    axiosConfig: { params: hasParams ? params : undefined },
    schema: containerResponseGuard<Account[]>(),
  });
}

/**
 * Boards of a connected Pinterest account (composer board picker). Disabled
 * until an account id is set. An empty list means the account has no boards;
 * a Pinterest API failure is a 502 (and an unreadable stored token a 409), so
 * the picker shows an error with retry instead of "No boards found".
 */
export function usePinterestBoards(accountId: string | null) {
  return useApiQuery<PinterestBoard[]>(
    socialKeys.detail("pinterest-boards", accountId ?? ""),
    `/api/accounts/${accountId}/pinterest/boards`,
    {
      enabled: Boolean(accountId),
      // Boards rarely change mid-compose.
      staleTime: 60_000,
      schema: containerResponseGuard<PinterestBoard[]>(),
    }
  );
}

// ── Posts ─────────────────────────────────────────────────────────────────────

export function usePosts(params?: {
  status?: string;
  page?: number;
  page_size?: number;
  /** Caption substring search (server ILIKE). */
  q?: string;
  /** Only posts with a target on this network. */
  platform?: string;
}) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const mergedParams = selectedBrandId ? { ...params, client_brand_id: selectedBrandId } : params;
  // Server returns the paginated envelope `{ items: Post[], total, page, page_size }`.
  // Type the hook so callers reach `.items` instead of expecting a bare
  // array — fixes the SocialOverview "o.map is not a function" runtime
  // crash that surfaced when the runtime-console-health audit started
  // running authenticated (see ops_deploy_warnings_followup handoff #1).
  return useApiQuery<{ items: Post[]; total?: number; page?: number; page_size?: number }>(
    socialKeys.list("posts", mergedParams as Record<string, unknown> | undefined),
    "/api/posts",
    {
      axiosConfig: { params: mergedParams },
      schema: objectResponseGuard<{ items: Post[]; total?: number; page?: number; page_size?: number }>(),
    }
  );
}

export function usePost(postId: string) {
  return useApiQuery<Post>(socialKeys.detail("posts", postId), `/api/posts/${postId}`, {
    // Non-UUID ids can only 422 — skip the call; PostDetail renders "not found".
    enabled: isUuid(postId),
    schema: objectResponseGuard<Post>(),
  });
}

// ── Calendar ──────────────────────────────────────────────────────────────────

/**
 * `contentFormat: "story"` asks the server for Stories only — it also turns OFF the server's
 * folding of a linked Story into its parent post, so every Story comes back as its own item.
 * Omitted (all / feed) keeps today's folded calendar; the Feed filter is applied client-side.
 */
export function useCalendar(start: string, end: string, contentFormat?: "story") {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params: Record<string, string> = { start, end };
  if (selectedBrandId) params.client_brand_id = selectedBrandId;
  if (contentFormat) params.content_format = contentFormat;
  // Server returns the wrapped envelope `{ items: CalendarPost[] }` — see
  // backend/servers/social/schemas/calendar.py::CalendarData. Typing this hook
  // as a bare `CalendarPost[]` was a lie (the unwrapped `data` is the {items}
  // OBJECT, not an array) and caused the "c.filter is not a function" crash in
  // ContentCalendar. Mirror the usePosts envelope fix: callers read `.items`.
  return useApiQuery<{ items: CalendarPost[] }>(socialKeys.list("calendar", params), "/api/calendar", {
    enabled: Boolean(start && end),
    axiosConfig: { params },
    schema: objectResponseGuard<{ items: CalendarPost[] }>(),
  });
}

// ── Analytics ─────────────────────────────────────────────────────────────────

// All analytics hooks scope to the selected client brand when one is
// active. The backend `/api/analytics/*` endpoints accept an optional
// `client_brand_id` filter; omitting it (no brand selected) returns the
// org-wide rollup. Tracked: manager-social backlog
// analytics-brand-scope-2026-06-04-001.
export function useAnalyticsOverview(start: string, end: string) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params = selectedBrandId ? { start, end, client_brand_id: selectedBrandId } : { start, end };
  return useApiQuery<AnalyticsOverview>(socialKeys.list("analytics-overview", params), "/api/analytics/overview", {
    enabled: Boolean(start && end),
    // Analytics snapshots are computed on a daily cadence — a 60s staleTime
    // avoids refetching the rollup every time the Dashboard tab re-mounts.
    staleTime: 60_000,
    axiosConfig: { params },
    schema: objectResponseGuard<AnalyticsOverview>(),
  });
}

export function usePlatformAnalytics(platform: string, start: string, end: string) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params = selectedBrandId ? { start, end, client_brand_id: selectedBrandId } : { start, end };
  return useApiQuery<AnalyticsSnapshot[]>(
    socialKeys.list("analytics-platform", { platform, ...params }),
    `/api/analytics/platform/${platform}`,
    {
      enabled: Boolean(platform && start && end),
      axiosConfig: { params },
      schema: containerResponseGuard<AnalyticsSnapshot[]>(),
    }
  );
}

export function useTopPosts(limit = 10) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params = selectedBrandId ? { limit, client_brand_id: selectedBrandId } : { limit };
  return useApiQuery<Post[]>(socialKeys.list("analytics-top-posts", params), "/api/analytics/posts/top", {
    axiosConfig: { params },
    schema: containerResponseGuard<Post[]>(),
  });
}

/**
 * Per-brand churn-risk rollup — deliberately org-wide (cross-client view).
 * Unlike the other analytics hooks above, this NEVER sends `client_brand_id`:
 * the whole point of the Retention tab is to surface every at-risk brand at
 * once, not filter down to whichever brand happens to be selected elsewhere
 * on the page. See backend/servers/social/routers/analytics.py::get_churn_risk.
 */
export function useChurnRisk() {
  return useApiQuery<ChurnRiskData>(socialKeys.list("analytics-churn-risk"), "/api/analytics/churn-risk", {
    // Rolled up from engagement timestamps on a slow cadence — a 60s
    // staleTime avoids refetching every time the Retention tab re-mounts.
    staleTime: 60_000,
    schema: objectResponseGuard<ChurnRiskData>(),
  });
}

/**
 * Day-0 → Day-14 "visible win" onboarding cadence (SOP SM-061) — org-wide
 * like `useChurnRisk`: every brand in its first `window_days` plus any brand
 * that never published. See
 * backend/servers/social/routers/analytics.py::get_onboarding.
 */
export function useOnboardingCadence() {
  return useApiQuery<OnboardingData>(socialKeys.list("analytics-onboarding"), "/api/analytics/onboarding", {
    staleTime: 60_000,
    schema: objectResponseGuard<OnboardingData>(),
  });
}

/**
 * Per-client monthly OUTCOMES report (retention memo lever L2). The report is a
 * per-client artifact, so the query only runs once a client brand is selected
 * (`clientBrandId` null → disabled). `month` is `YYYY-MM`.
 * See backend/servers/social/services/monthly_report.py.
 */
export function useMonthlyReport(clientBrandId: string | null, month: string) {
  const params = { client_brand_id: clientBrandId ?? "", month };
  return useApiQuery<MonthlyReportData>(
    socialKeys.list("analytics-monthly-report", params),
    "/api/analytics/monthly-report",
    {
      enabled: Boolean(clientBrandId && month),
      // A closed month's numbers only move when the nightly collector
      // backfills — 5 min staleTime avoids refetching on every tab switch.
      staleTime: 300_000,
      axiosConfig: { params },
      schema: objectResponseGuard<MonthlyReportData>(),
    }
  );
}

// ── Media ─────────────────────────────────────────────────────────────────────

export function useMedia(limit = 50) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params: Record<string, unknown> = { limit };
  if (selectedBrandId) params.client_brand_id = selectedBrandId;
  return useApiQuery<MediaItem[]>(socialKeys.list("media", params), "/api/media", {
    axiosConfig: { params },
    schema: containerResponseGuard<MediaItem[]>(),
  });
}

// ── Templates ─────────────────────────────────────────────────────────────────

export function useTemplates() {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params = selectedBrandId ? { client_brand_id: selectedBrandId } : undefined;
  return useApiQuery<Template[]>(socialKeys.list("templates", params), "/api/templates", {
    axiosConfig: { params },
    schema: containerResponseGuard<Template[]>(),
  });
}

// ── Inbox ─────────────────────────────────────────────────────────────────────

export interface Comment {
  id: string;
  org_id: number;
  account_id: string;
  account_name?: string;
  post_platform_id: string | null;
  platform: string;
  platform_comment_id: string | null;
  author_name: string | null;
  author_id_external: string | null;
  content: string;
  sentiment: string | null;
  replied: boolean;
  reply_content: string | null;
  replied_at: string | null;
  is_hidden: boolean;
  posted_at: string;
  created_at: string;
  /**
   * AI-drafted SM-006 reply (classifier). Only present for unreplied, visible,
   * still-fresh comments. A DRAFT — the human must choose to use it and press
   * Reply; nothing is ever sent automatically. (Moved here from the Ops
   * approval queue 2026-09-25.)
   */
  suggested_reply?: SuggestedReply | null;
}

import type { SuggestedReply } from "../types/social";

export type { SuggestedReply };

interface InboxFilters {
  platform?: string;
  replied?: boolean;
  page?: number;
  page_size?: number;
}

export function useInbox(filters?: InboxFilters) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const mergedFilters = selectedBrandId ? { ...filters, client_brand_id: selectedBrandId } : filters;
  // Server returns the wrapped envelope `{ items: Comment[] }` — see
  // backend/servers/social/schemas/inbox.py::InboxListData.items. Typing this
  // hook as a bare `Comment[]` was a lie (the unwrapped `data` is the
  // {items} OBJECT, not an array) and causes "x.filter is not a function"
  // crashes in Inbox. Callers must read `.items` (with Array.isArray guard).
  return useApiQuery<{ items: Comment[] }>(
    socialKeys.list("inbox", mergedFilters as Record<string, unknown> | undefined),
    "/api/inbox",
    { axiosConfig: { params: mergedFilters }, schema: objectResponseGuard<{ items: Comment[] }>() }
  );
}

export function useInboxUnreadCount() {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const params = selectedBrandId ? { client_brand_id: selectedBrandId } : undefined;
  return useApiQuery<{ unread: number }>(
    socialKeys.detail("inbox", selectedBrandId ? `unread-count:${selectedBrandId}` : "unread-count"),
    "/api/inbox/unread-count",
    {
      refetchInterval: 30_000,
      axiosConfig: { params },
      schema: objectResponseGuard<{ unread: number }>(),
    }
  );
}

// ── Copywriter ───────────────────────────────────────────────────────────────

export function useCopywriterConfig() {
  return useApiQuery<CopywriterConfig>(socialKeys.detail("copywriter", "config"), "/api/copywriter/config", {
    schema: objectResponseGuard<CopywriterConfig>(),
  });
}

// ── Copywriter Analytics ────────────────────────────────────────────────────

import type { CopywriterAnalytics, CopywriterInsight, GenerationHistoryList } from "../types/social";

export function useCopywriterAnalytics(params?: { start?: string; end?: string }) {
  return useApiQuery<CopywriterAnalytics>(
    socialKeys.list("copywriter-analytics", params as Record<string, unknown> | undefined),
    "/api/copywriter/analytics",
    { axiosConfig: { params }, schema: objectResponseGuard<CopywriterAnalytics>() }
  );
}

export function useCopywriterInsights() {
  return useApiQuery<CopywriterInsight[]>(
    socialKeys.list("copywriter-insights"),
    "/api/copywriter/analytics/insights",
    {
      schema: containerResponseGuard<CopywriterInsight[]>(),
    }
  );
}

// ── Content Generation ──────────────────────────────────────────────────────

export function useCopywriterHistory(params?: { page?: number; page_size?: number }, options?: { enabled?: boolean }) {
  return useApiQuery<GenerationHistoryList>(
    socialKeys.list("copywriter-history", params as Record<string, unknown> | undefined),
    "/api/copywriter/history",
    { axiosConfig: { params }, enabled: options?.enabled, schema: objectResponseGuard<GenerationHistoryList>() }
  );
}

// ── Brand Voice ──────────────────────────────────────────────────────────────

import type {
  BrandVoiceProfile,
  CompetitorAccount,
  DocumentEntry,
  IngestionOverview,
  KnowledgeHealth,
  QualityReport,
} from "../types/social";

export function useBrandProfile(accountId?: string) {
  return useApiQuery<Partial<BrandVoiceProfile> | null>(
    socialKeys.detail("brand-profile", accountId ?? "all"),
    "/api/brand/profile",
    {
      axiosConfig: { params: accountId ? { account_id: accountId } : undefined },
      schema: objectResponseGuard<Partial<BrandVoiceProfile> | null>(),
    }
  );
}

export function useCompetitors(accountId?: string) {
  return useApiQuery<CompetitorAccount[]>(
    socialKeys.list("competitors", accountId ? { account_id: accountId } : undefined),
    "/api/brand/competitors",
    {
      axiosConfig: { params: accountId ? { account_id: accountId } : undefined },
      schema: containerResponseGuard<CompetitorAccount[]>(),
    }
  );
}

// ── Post Ingestion ──────────────────────────────────────────────────────────

export function useIngestionStatus() {
  return useApiQuery<IngestionOverview>(
    socialKeys.detail("knowledge", "ingestion-status"),
    "/api/knowledge/ingestion-status",
    { schema: objectResponseGuard<IngestionOverview>() }
  );
}

// ── Documents ───────────────────────────────────────────────────────────────

/** Ingested documents; scoped to the selected client brand when one is chosen. */
export function useDocuments() {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const brandId = selectedBrandId && isUuid(selectedBrandId) ? selectedBrandId : null;
  return useApiQuery<DocumentEntry[]>(
    socialKeys.list("knowledge-documents", { clientBrandId: brandId }),
    "/api/knowledge/documents",
    {
      axiosConfig: { params: brandId ? { client_brand_id: brandId } : undefined },
      schema: containerResponseGuard<DocumentEntry[]>(),
    }
  );
}

// ── Knowledge Health ────────────────────────────────────────────────────────

export function useKnowledgeHealth() {
  return useApiQuery<KnowledgeHealth>(socialKeys.detail("knowledge", "health"), "/api/knowledge/health", {
    schema: objectResponseGuard<KnowledgeHealth>(),
  });
}

export function useKnowledgeQuality() {
  return useApiQuery<QualityReport>(socialKeys.detail("knowledge", "quality"), "/api/knowledge/quality", {
    schema: objectResponseGuard<QualityReport>(),
  });
}

export interface LearningStats {
  total_generations: number;
  used_generations: number;
  usage_rate: number;
  with_engagement: number;
  avg_feedback_score: number | null;
  stale_chunks: number;
  feedback_knowledge_chunks: number;
}

export function useLearningStats() {
  return useApiQuery<LearningStats>(socialKeys.detail("knowledge", "learning-stats"), "/api/knowledge/learning-stats", {
    schema: objectResponseGuard<LearningStats>(),
  });
}

// ── Knowledge Base ──────────────────────────────────────────────────────────

import type {
  ArchivedPostList,
  ArchiveStats,
  DesignInsights,
  KnowledgeChunkList,
  KnowledgeStats,
  ScrapeJob,
  ScrapeSchedule,
} from "../types/social";

interface KnowledgeChunkFilters {
  chunk_type?: string;
  account_id?: string;
  page?: number;
  page_size?: number;
}

export function useKnowledgeStats() {
  return useApiQuery<KnowledgeStats>(socialKeys.detail("knowledge", "stats"), "/api/knowledge/stats", {
    schema: objectResponseGuard<KnowledgeStats>(),
  });
}

export function useKnowledgeChunks(params?: KnowledgeChunkFilters) {
  return useApiQuery<KnowledgeChunkList>(
    socialKeys.list("knowledge-chunks", params as Record<string, unknown> | undefined),
    "/api/knowledge/chunks",
    { axiosConfig: { params }, schema: objectResponseGuard<KnowledgeChunkList>() }
  );
}

// ── Hashtag Research ─────────────────────────────────────────────────────────

import type { HashtagResearchResult, PopularHashtag } from "../types/social";

export function useHashtagResearch(params: { topic: string; platform?: string; account_id?: string }) {
  return useApiQuery<HashtagResearchResult>(
    socialKeys.list("hashtag-research", params as Record<string, unknown>),
    "/api/copywriter/hashtags",
    {
      enabled: Boolean(params.topic),
      axiosConfig: { params },
      schema: objectResponseGuard<HashtagResearchResult>(),
    }
  );
}

export function usePopularHashtags(params?: { limit?: number; platform?: string }) {
  return useApiQuery<{ hashtags: PopularHashtag[] }>(
    socialKeys.list("popular-hashtags", params as Record<string, unknown> | undefined),
    "/api/copywriter/hashtags/popular",
    { axiosConfig: { params }, schema: objectResponseGuard<{ hashtags: PopularHashtag[] }>() }
  );
}

// ── Copywriter Chat ──────────────────────────────────────────────────────────

import type { ChatSessionDetail, ChatSessionList } from "../types/social";

export function useChatSessions(params?: { page?: number; page_size?: number }) {
  const selectedBrandId = useClientBrandStore((s) => s.selectedBrandId);
  const mergedParams = selectedBrandId ? { ...params, client_brand_id: selectedBrandId } : params;
  return useApiQuery<ChatSessionList>(
    socialKeys.list("chat-sessions", mergedParams as Record<string, unknown> | undefined),
    "/api/copywriter/chat/sessions",
    { axiosConfig: { params: mergedParams }, schema: objectResponseGuard<ChatSessionList>() }
  );
}

export function useChatSession(sessionId: string | null) {
  return useApiQuery<ChatSessionDetail>(
    socialKeys.detail("chat-sessions", sessionId ?? ""),
    `/api/copywriter/chat/sessions/${sessionId}`,
    { enabled: Boolean(sessionId), schema: objectResponseGuard<ChatSessionDetail>() }
  );
}

// ── Instagram Archive ────────────────────────────────────────────────────────

export interface ArchivePostFilters {
  username?: string;
  media_type?: string;
  date_from?: string;
  date_to?: string;
  hashtag?: string;
  search?: string;
  sort_by?: "date" | "likes" | "comments";
  page?: number;
  per_page?: number;
}

export function useArchiveJobs(params?: { status?: string; page?: number; per_page?: number }) {
  // Server returns the wrapped envelope `{ jobs: ScrapeJob[]; total: number; page: number }` — see
  // backend/servers/social/schemas/archive.py::ScrapeJobListResponse. Field is `.jobs`,
  // NOT `.items`. Typing as bare array caused "x.filter is not a function" in Archive.
  // Callers must read `.jobs` (with Array.isArray guard).
  return useApiQuery<{ jobs: ScrapeJob[]; total: number; page: number }>(
    socialKeys.list("archive-jobs", params as Record<string, unknown> | undefined),
    "/api/instagram-archive/jobs",
    { axiosConfig: { params }, schema: objectResponseGuard<{ jobs: ScrapeJob[]; total: number; page: number }>() }
  );
}

export function useArchivedPosts(params?: ArchivePostFilters) {
  return useApiQuery<ArchivedPostList>(
    socialKeys.list("archived-posts", params as Record<string, unknown> | undefined),
    "/api/instagram-archive/posts",
    {
      axiosConfig: { params },
      // Keep the current grid on screen while the next page / filter loads (no blank flash).
      placeholderData: keepPreviousData,
      schema: objectResponseGuard<ArchivedPostList>(),
    }
  );
}

export function useArchiveStats(username?: string) {
  return useApiQuery<ArchiveStats>(
    socialKeys.list("archive-stats", username ? { username } : undefined),
    "/api/instagram-archive/stats",
    {
      axiosConfig: { params: username ? { username } : undefined },
      schema: objectResponseGuard<ArchiveStats>(),
    }
  );
}

// ── Design Analysis ─────────────────────────────────────────────────────────

export function useDesignInsights(username?: string) {
  return useApiQuery<DesignInsights>(
    socialKeys.list("design-insights", username ? { username } : undefined),
    "/api/instagram-archive/design-insights",
    {
      axiosConfig: { params: username ? { username } : undefined },
      schema: objectResponseGuard<DesignInsights>(),
    }
  );
}

// ── Brand Consistency ────────────────────────────────────────────────────────

import type { BrandConsistency, MonitoringData, VisualEvolution } from "../types/social";

export function useBrandConsistency(username?: string) {
  return useApiQuery<BrandConsistency>(
    socialKeys.list("brand-consistency", username ? { username } : undefined),
    "/api/instagram-archive/brand-consistency",
    {
      axiosConfig: { params: username ? { username } : undefined },
      schema: objectResponseGuard<BrandConsistency>(),
    }
  );
}

// ── Visual Evolution ────────────────────────────────────────────────────────

export function useVisualEvolution(username?: string) {
  return useApiQuery<VisualEvolution>(
    socialKeys.list("visual-evolution", username ? { username } : undefined),
    "/api/instagram-archive/visual-evolution",
    {
      axiosConfig: { params: username ? { username } : undefined },
      schema: objectResponseGuard<VisualEvolution>(),
    }
  );
}

// ── Monitoring ──────────────────────────────────────────────────────────────

export function useArchiveMonitoring() {
  return useApiQuery<MonitoringData>(socialKeys.list("archive-monitoring"), "/api/instagram-archive/monitoring", {
    refetchInterval: 60_000,
    schema: objectResponseGuard<MonitoringData>(),
  });
}

// ── Scrape Schedules ────────────────────────────────────────────────────────

export function useScrapeSchedules() {
  // Server returns the wrapped envelope `{ schedules: ScrapeSchedule[] }` — see
  // backend/servers/social/schemas/archive.py::ScheduleListResponse. Field is
  // `.schedules`, NOT `.items`. Typing as bare array caused "x.length is not a
  // function" in SchedulesPanel. Callers must read `.schedules` (with Array.isArray guard).
  return useApiQuery<{ schedules: ScrapeSchedule[] }>(
    socialKeys.list("scrape-schedules"),
    "/api/instagram-archive/schedules",
    { schema: objectResponseGuard<{ schedules: ScrapeSchedule[] }>() }
  );
}

// ── Tenant Context ─────────────────────────────────────────────────────────────

export interface TenantContext {
  org_id: number;
  org_name: string;
  hostname: string;
  product: string;
  logo_url: string | null;
  plan_tier: string;
  powered_by: boolean;
}

export function useTenantContext() {
  return useApiQuery<TenantContext>(socialKeys.detail("tenant", "context"), "/api/tenant-context", {
    schema: objectResponseGuard<TenantContext>(),
  });
}

// ── User Settings ────────────────────────────────────────────────────────────

/** Per-user Social Media settings (General + Notifications). Persisted to
 * social_media.user_settings via /api/user-settings. */
export function useUserSettings() {
  return useApiQuery<UserSettings>(socialKeys.detail("user-settings", "self"), "/api/user-settings", {
    schema: objectResponseGuard<UserSettings>(),
  });
}

// ── Org Settings ─────────────────────────────────────────────────────────────

/** Org-wide Social Media settings (currently: the UTM auto-tagging default),
 * shared by every user of the org. Persisted to social_media.org_settings via
 * /api/org-settings. */
export function useOrgSettings() {
  return useApiQuery<OrgSettings>(socialKeys.detail("org-settings", "self"), "/api/org-settings", {
    schema: objectResponseGuard<OrgSettings>(),
  });
}
